import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { supabase } from '@/config/supabase'
import { formatCurrency, formatDate } from '@/lib/formatters'
import { CheckCircle2, AlertTriangle, ShieldCheck, Clock, User, FileText, Globe } from 'lucide-react'

export function PortalValidacaoPage() {
    const { id } = useParams()
    const [loading, setLoading] = useState(true)
    const [contrato, setContrato] = useState<any>(null)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        async function fetchContrato() {
            if (!id) return
            try {
                const { data, error } = await supabase
                    .schema('veicular_system')
                    .from('veic_contratos')
                    .select('*, associado:veic_associados(nome, cpf)')
                    .eq('id', id)
                    .single()

                if (error || !data) throw new Error('Documento não encontrado ou inválido.')
                setContrato(data)
            } catch (err: any) {
                setError(err.message)
            } finally {
                setLoading(false)
            }
        }
        fetchContrato()
    }, [id])

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-surface-50 p-6">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-surface-700/60 font-medium">Validando documento...</p>
                </div>
            </div>
        )
    }

    if (error || !contrato) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-surface-50 p-6 text-center">
                <div className="max-w-md bg-white p-8 rounded-3xl shadow-xl border border-danger-100">
                    <div className="w-16 h-16 bg-danger-100 text-danger-600 rounded-full flex items-center justify-center mx-auto mb-6">
                        <AlertTriangle size={32} />
                    </div>
                    <h1 className="text-2xl font-bold text-surface-900 mb-2">Falha na Validação</h1>
                    <p className="text-surface-700/60 mb-6">{error || 'Identificador de documento inválido ou expirado.'}</p>
                    <Link to="/portal/login" className="btn btn-primary w-full">Voltar ao Portal</Link>
                </div>
            </div>
        )
    }

    const sig = contrato.metadata?.assinatura_digital
    const formatBR = (dateStr: string) => {
        try {
            return new Date(dateStr).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })
        } catch (e) { return dateStr }
    }

    return (
        <div className="min-h-screen bg-surface-50 p-4 md:p-8 animate-fade-in">
            <div className="max-w-2xl mx-auto space-y-6">
                {/* Header de Status */}
                <div className="bg-success-600 text-white p-8 rounded-3xl shadow-xl text-center relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-4 opacity-10">
                        <ShieldCheck size={120} />
                    </div>
                    <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 backdrop-blur-sm">
                        <CheckCircle2 size={32} />
                    </div>
                    <h1 className="text-2xl font-bold mb-1">Documento Autêntico</h1>
                    <p className="text-white/80 text-sm">Validado com sucesso via ICP-Brasil Eletrônico</p>
                </div>

                {/* Dados do Contrato */}
                <div className="bg-white p-6 rounded-3xl shadow-sm border border-surface-100 space-y-6">
                    <div className="flex items-center gap-3 pb-4 border-b border-surface-50">
                        <FileText className="text-primary-600" />
                        <h2 className="font-bold text-surface-900">Informações do Contrato</h2>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <InfoItem label="Identificador" value={contrato.external_reference || contrato.id} />
                        <InfoItem label="Serviço" value={contrato.metadata?.servico_nome || 'Serviço Prestado'} />
                        <InfoItem label="Valor Mensal" value={formatCurrency(contrato.valor_total_mensal)} />
                        <InfoItem label="Data de Início" value={formatDate(contrato.data_inicio)} />
                    </div>
                </div>

                {/* Evidências de Assinatura */}
                <div className="bg-white p-6 rounded-3xl shadow-sm border border-surface-100 space-y-6">
                    <div className="flex items-center gap-3 pb-4 border-b border-surface-50">
                        <ShieldCheck className="text-success-600" />
                        <h2 className="font-bold text-surface-900">Assinatura Digital</h2>
                    </div>

                    <div className="space-y-4">
                        <EvidenciaItem 
                            icon={<User size={16} />} 
                            label="Signatário" 
                            value={`${contrato.associado?.nome} (CPF: ${contrato.associado?.cpf})`} 
                        />
                        <EvidenciaItem 
                            icon={<Clock size={16} />} 
                            label="Data/Hora do Aceite" 
                            value={`${formatBR(sig?.timestamp)} (Horário de Brasília)`} 
                        />
                        <EvidenciaItem 
                            icon={<Globe size={16} />} 
                            label="Endereço IP" 
                            value={sig?.ip_real || sig?.ip_estimado || 'Gravado no Servidor'} 
                        />
                    </div>

                    <div className="mt-6 p-4 bg-surface-50 rounded-2xl border border-surface-100">
                        <p className="text-[10px] font-bold text-surface-400 uppercase tracking-widest mb-2">Hash de Integridade (SHA-256)</p>
                        <p className="text-xs font-mono text-surface-600 break-all leading-relaxed">
                            {sig?.hash || 'Integridade garantida via log de auditoria do sistema.'}
                        </p>
                    </div>
                </div>

                <div className="text-center text-surface-400 text-xs px-6">
                    <p>Esta página confirma a autenticidade e a integridade do contrato eletrônico gerado pelo Portal ACSM Multiveicular em conformidade com a MP 2.200-2/2001.</p>
                </div>
            </div>
        </div>
    )
}

function InfoItem({ label, value }: { label: string, value: string }) {
    return (
        <div>
            <p className="text-xs text-surface-500 mb-0.5">{label}</p>
            <p className="font-semibold text-surface-900">{value}</p>
        </div>
    )
}

function EvidenciaItem({ icon, label, value }: { icon: any, label: string, value: string }) {
    return (
        <div className="flex items-start gap-3">
            <div className="mt-0.5 text-surface-400">{icon}</div>
            <div>
                <p className="text-xs text-surface-500">{label}</p>
                <p className="text-sm font-medium text-surface-900 leading-tight">{value}</p>
            </div>
        </div>
    )
}
